/**
 * Turning a stored reply body into something a person can read (AB#22441).
 *
 * Mail clients mark quoted material by prefixing each line with ">", and a
 * quote of a quote gets another one (">>"). The inbox stores the body exactly
 * as received, so rendering it raw shows every quoted line with its marks, and
 * the "On ... wrote:" header and the quoted thread run straight into the list
 * preview. Here we split what the person typed from the thread under it and
 * drop the marks.
 *
 * The markers mirror backend `strip_quoted_reply` (services/sentiment_analysis),
 * which does the same cut for classification. They are line-anchored on
 * purpose: "wrote:" and "From:" turn up in ordinary prose, and cutting at them
 * mid-sentence would truncate a real reply.
 */

export interface ParsedReplyBody {
  /** What the person typed. Never contains a ">" quote prefix. */
  reply: string
  /** The thread quoted under it, already readable. Empty when there is none. */
  quoted: string
}

const QUOTE_MARKERS: RegExp[] = [
  // "On Mon, 5 Oct 2026 at 10:04, Jane <jane@x.com> wrote:" (Gmail/Apple),
  // which clients often wrap so that "wrote:" lands on the next line.
  /^[ \t]*On\b[^\n]{0,200}(?:\n[ \t]*[^\n]{0,100})?\bwrote:[ \t]*$/im,
  /^[ \t]*-{2,}\s*Original Message\s*-{2,}/im,
  /^[ \t]*Begin forwarded message:/im,
  // Outlook's header block. A bare "From: the team" line is something a human
  // writes, so a Sent/Date/To line must follow within three lines.
  /^From:[^\n]*(?:\n[^\n]*){0,3}?\n[ \t]*(?:Sent|Date|To):/im,
  /^[ \t]*_{8,}[ \t]*$/m,
  // A ">"-quoted line.
  /^[ \t]*>/m,
]

const MAX_PEELS = 3

const normalise = (body: string): string => body.replace(/\r\n?/g, '\n')

/** Remove ">" quote prefixes at every depth ("> > x", ">> x", ">x" all become "x"). */
export function readableText(text: string): string {
  return text
    .split('\n')
    .map((line) => line.replace(/^[ \t]*(?:>[ \t]*)+/, ''))
    .join('\n')
}

/** Remove exactly one quote level, so a nested thread keeps its own ">" for the next cut. */
function peelOneLevel(text: string): string {
  return text
    .split('\n')
    .map((line) => line.replace(/^[ \t]*>[ ]?/, ''))
    .join('\n')
}

function cutAtFirstMarker(text: string): { reply: string; quoted: string } {
  let cut = text.length
  for (const marker of QUOTE_MARKERS) {
    const hit = marker.exec(text)
    if (hit && hit.index < cut) cut = hit.index
  }
  if (cut >= text.length) return { reply: text.trim(), quoted: '' }
  return {
    reply: text.slice(0, cut).trim(),
    quoted: readableText(text.slice(cut)).trim(),
  }
}

/**
 * Split a reply into what was typed and the thread quoted under it.
 *
 * When the whole body is quoted (the person replied by forwarding, or their
 * client put everything under ">"), the typed part is empty. Rather than show
 * nothing, peel one quote level and look again: the outermost level is then the
 * reply and anything nested deeper stays folded away. If nothing is left after
 * a few peels, the body is shown whole, readable, like the backend does for
 * the bottom-poster.
 */
export function parseReplyBody(body?: string | null): ParsedReplyBody {
  let text = normalise(body ?? '')
  if (!text.trim()) return { reply: '', quoted: '' }

  for (let i = 0; i <= MAX_PEELS; i += 1) {
    const { reply, quoted } = cutAtFirstMarker(text)
    if (reply) return { reply, quoted }
    const peeled = peelOneLevel(text)
    if (peeled === text) break
    text = peeled
  }
  return { reply: readableText(text).trim(), quoted: '' }
}
